// components/ConvertLeadModal.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  Building2,
  User,
  Briefcase,
  Loader,
  Search,
  DollarSign,
  FileText,
  Target,
  TrendingUp,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const ConvertLeadModal = ({ lead, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accountSearch, setAccountSearch] = useState("");
  const [contactSearch, setContactSearch] = useState("");

  const [accountOption, setAccountOption] = useState("create");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [accountFormData, setAccountFormData] = useState({
    accountName: lead.companyName || lead.fullName || "",
    accountType: "Prospect",
    accountIndustry: lead.companyIndustry || "",
    accountWebsite: lead.companyWebsite || "",
    accountPhone: lead.phone || "",
    accountBillingStreet: lead.street || "",
    accountBillingCity: lead.city || "",
    accountBillingState: lead.state || "",
    accountBillingPostalCode: lead.postalCode || "",
    accountBillingCountry: lead.country || "",
  });

  const [contactOption, setContactOption] = useState("create");
  const [selectedContactId, setSelectedContactId] = useState("");

  const [createOpportunity, setCreateOpportunity] = useState(true);
  const [opportunityFormData, setOpportunityFormData] = useState({
    opportunityName: `${lead.companyName || lead.fullName} - Opportunity`,
    opportunityAmount: lead.amount || "",
    opportunityCurrency: lead.currency || "GBP",
    opportunityProbability: lead.probability || 50,
    opportunityExpectedRevenue: lead.expectedRevenue || "",
    opportunityStage: lead.opportunityStage || "qualification",
    opportunityPriority: lead.opportunityPriority || "medium",
    opportunityType: lead.opportunityType || "",
    opportunityExpectedCloseDate:
      lead.expectedCloseDate ||
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
    opportunityDescription:
      lead.description || `Converted from lead: ${lead.fullName || lead.email}`,
    nextStep: lead.nextStep || "",
    primaryCampaignSource: lead.primaryCampaignSource || "",
    numberOfHandsets: lead.numberOfHandsets || "",
    monthlySpendWithCurrentProvider: lead.monthlySpendWithCurrentProvider || "",
    currentPhoneSystemAge: lead.currentPhoneSystemAge || "",
    unitsSold: lead.unitsSold || "",
    contractTermLeft: lead.contractTermLeft || "",
    contractEndDate: lead.contractEndDate || "",
    callerNotes: lead.callerNotes || "",
    booked: lead.booked || false,
    bookedDate: lead.bookedDate || "",
    twoMinChallenge: lead.twoMinChallenge || false,
    twoMinChallengeDate: lead.twoMinChallengeDate || "",
    nonQualSat: lead.nonQualSat || false,
    satNonQualDate: lead.satNonQualDate || "",
    qualSat: lead.qualSat || false,
    qualSatDate: lead.qualSatDate || "",
    sold: lead.sold || false,
    soldDate: lead.soldDate || "",
    installComplete: lead.installComplete || false,
    installationDate: lead.installationDate || "",
    notSat: lead.notSat || false,
  });

  useEffect(() => {
    if (accountOption === "existing") fetchAccounts();
  }, [accountOption, accountSearch]);

  useEffect(() => {
    if (contactOption === "existing") fetchContacts();
  }, [contactOption, contactSearch, selectedAccountId]);

  const fetchAccounts = async () => {
    try {
      const response = await apiService.getAccounts({
        limit: 100,
        search: accountSearch,
      });
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const fetchContacts = async () => {
    try {
      const params = { limit: 100, search: contactSearch };
      if (selectedAccountId && accountOption === "existing") {
        params.accountId = selectedAccountId;
      }
      const response = await apiService.getContacts(params);
      setContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const handleConvert = async () => {
    setLoading(true);
    try {
      const conversionData = {
        createAccount: accountOption === "create",
        accountId: accountOption === "existing" ? selectedAccountId : undefined,
        ...accountFormData,
        createContact: contactOption === "create",
        contactId: contactOption === "existing" ? selectedContactId : undefined,
        createOpportunity,
        doNotCreateOpportunity: !createOpportunity,
        ...opportunityFormData,
        status: "converted",
      };
      await apiService.convertLead(lead.id, conversionData);
      onSuccess();
    } catch (error) {
      console.error("Error converting lead:", error);
      alert(
        error.response?.data?.message ||
          "Failed to convert lead. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpportunityChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOpportunityFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);
  const selectedContact = contacts.find((con) => con.id === selectedContactId);

  const getCurrencySymbol = (currency = "GBP") => {
    const symbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
    };
    return symbols[currency] || currency;
  };

  // Theme helpers
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
    isDarkMode
      ? "bg-[#13161B] border-gray-700 text-white placeholder-gray-500"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDarkMode ? "text-gray-300" : "text-gray-700"
  }`;

  const radioCardClass = `flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
    isDarkMode
      ? "bg-[#13161B] border-gray-700 hover:border-blue-500"
      : "bg-gray-50 border-gray-200 hover:border-blue-400"
  }`;

  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const panelBg = isDarkMode ? "bg-gray-800/30" : "bg-gray-50";
  const dropdownBg = isDarkMode
    ? "bg-[#13161B] border-gray-700"
    : "bg-white border-gray-200";

  const skippedStep = (icon) => (
    <div className="text-center py-12">
      {React.createElement(icon, {
        size: 48,
        className: `mx-auto mb-4 ${textSecondary}`,
      })}
      <p className={textSecondary}>
        Opportunity creation is disabled. Click Next to proceed with conversion.
      </p>
    </div>
  );

  // Step 1: Record Selection
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3
          className={`text-lg font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}
        >
          <Building2 size={20} className="text-blue-400" />
          Account
        </h3>
        <div className="space-y-4">
          {[
            {
              value: "create",
              label: "Create New Account",
              desc: "Create a new account record from this lead",
            },
            {
              value: "existing",
              label: "Use Existing Account",
              desc: "Link this lead to an existing account",
            },
            {
              value: "none",
              label: "Don't Create Account",
              desc: "Convert without creating an account",
            },
          ].map((opt) => (
            <label key={opt.value} className={radioCardClass}>
              <input
                type="radio"
                name="accountOption"
                value={opt.value}
                checked={accountOption === opt.value}
                onChange={(e) => setAccountOption(e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className={`font-medium mb-1 ${textPrimary}`}>
                  {opt.label}
                </div>
                <div className={`text-sm ${textSecondary}`}>{opt.desc}</div>
              </div>
            </label>
          ))}

          {accountOption === "existing" && (
            <div className="mt-4 space-y-3">
              <div className="relative">
                <input
                  type="text"
                  value={selectedAccount ? selectedAccount.name : accountSearch}
                  onChange={(e) => {
                    setAccountSearch(e.target.value);
                    if (!e.target.value) setSelectedAccountId("");
                  }}
                  className={`${inputClass} pl-10`}
                  placeholder="Search accounts..."
                />
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
              </div>

              {!selectedAccountId && accounts.length > 0 && (
                <div
                  className={`max-h-60 overflow-y-auto border rounded-lg ${dropdownBg}`}
                >
                  {accounts.map((account) => (
                    <div
                      key={account.id}
                      className={`px-4 py-3 cursor-pointer transition-colors border-b last:border-b-0 ${borderColor} ${
                        isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedAccountId(account.id)}
                    >
                      <div className={`font-medium ${textPrimary}`}>
                        {account.name}
                      </div>
                      {account.type && (
                        <div className={`text-xs mt-1 ${textSecondary}`}>
                          {account.type}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {selectedAccount && (
                <div className="p-4 bg-blue-600/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`font-medium ${textPrimary}`}>
                        {selectedAccount.name}
                      </div>
                      {selectedAccount.type && (
                        <div className="text-blue-400 text-sm mt-1">
                          {selectedAccount.type}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedAccountId("");
                        setAccountSearch("");
                      }}
                      className={`transition-colors ${textSecondary} hover:${textPrimary}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div>
        <h3
          className={`text-lg font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}
        >
          <User size={20} className="text-purple-400" />
          Contact
        </h3>
        <div className="space-y-4">
          {[
            {
              value: "create",
              label: "Create New Contact",
              desc: "Create a new contact from this lead",
            },
            {
              value: "existing",
              label: "Use Existing Contact",
              desc: "Link this lead to an existing contact",
            },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`${radioCardClass} hover:border-purple-500`}
            >
              <input
                type="radio"
                name="contactOption"
                value={opt.value}
                checked={contactOption === opt.value}
                onChange={(e) => setContactOption(e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className={`font-medium mb-1 ${textPrimary}`}>
                  {opt.label}
                </div>
                <div className={`text-sm ${textSecondary}`}>{opt.desc}</div>
              </div>
            </label>
          ))}

          {contactOption === "existing" && (
            <div className="mt-4 space-y-3">
              <div className="relative">
                <input
                  type="text"
                  value={
                    selectedContact
                      ? `${selectedContact.firstName} ${selectedContact.lastName}`
                      : contactSearch
                  }
                  onChange={(e) => {
                    setContactSearch(e.target.value);
                    if (!e.target.value) setSelectedContactId("");
                  }}
                  className={`${inputClass} pl-10`}
                  placeholder="Search contacts..."
                />
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
              </div>

              {!selectedContactId && contacts.length > 0 && (
                <div
                  className={`max-h-60 overflow-y-auto border rounded-lg ${dropdownBg}`}
                >
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className={`px-4 py-3 cursor-pointer transition-colors border-b last:border-b-0 ${borderColor} ${
                        isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedContactId(contact.id)}
                    >
                      <div className={`font-medium ${textPrimary}`}>
                        {contact.firstName} {contact.lastName}
                      </div>
                      <div className={`text-xs mt-1 ${textSecondary}`}>
                        {contact.email}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedContact && (
                <div className="p-4 bg-purple-600/10 border border-purple-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`font-medium ${textPrimary}`}>
                        {selectedContact.firstName} {selectedContact.lastName}
                      </div>
                      <div className="text-purple-400 text-sm mt-1">
                        {selectedContact.email}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedContactId("");
                        setContactSearch("");
                      }}
                      className={`transition-colors ${textSecondary}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div>
        <h3
          className={`text-lg font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}
        >
          <Briefcase size={20} className="text-green-400" />
          Opportunity
        </h3>
        <label className={`${radioCardClass} hover:border-green-500`}>
          <input
            type="checkbox"
            checked={createOpportunity}
            onChange={(e) => setCreateOpportunity(e.target.checked)}
            className="mt-1"
          />
          <div className="flex-1">
            <div className={`font-medium mb-1 ${textPrimary}`}>
              Create Opportunity
            </div>
            <div className={`text-sm ${textSecondary}`}>
              Create a new sales opportunity from this lead
            </div>
          </div>
        </label>
      </div>
    </div>
  );

  // Step 2: Account & Contact Details
  const renderStep2 = () => (
    <div className="space-y-6">
      {accountOption === "create" && (
        <div>
          <h3
            className={`text-lg font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}
          >
            <Building2 size={20} className="text-blue-400" />
            Account Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelClass}>
                Account Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={accountFormData.accountName}
                onChange={(e) =>
                  setAccountFormData({
                    ...accountFormData,
                    accountName: e.target.value,
                  })
                }
                className={inputClass}
                placeholder="Enter account name"
              />
            </div>
            <div>
              <label className={labelClass}>Account Type</label>
              <select
                value={accountFormData.accountType}
                onChange={(e) =>
                  setAccountFormData({
                    ...accountFormData,
                    accountType: e.target.value,
                  })
                }
                className={inputClass}
              >
                <option value="Prospect">Prospect</option>
                <option value="Customer - Direct">Customer - Direct</option>
                <option value="Customer - Channel">Customer - Channel</option>
                <option value="Channel Partner / Reseller">
                  Channel Partner / Reseller
                </option>
                <option value="Installation Partner">
                  Installation Partner
                </option>
                <option value="Technology Partner">Technology Partner</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Industry</label>
              <input
                type="text"
                value={accountFormData.accountIndustry}
                onChange={(e) =>
                  setAccountFormData({
                    ...accountFormData,
                    accountIndustry: e.target.value,
                  })
                }
                className={inputClass}
                placeholder="e.g., Technology"
              />
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <input
                type="tel"
                value={accountFormData.accountPhone}
                onChange={(e) =>
                  setAccountFormData({
                    ...accountFormData,
                    accountPhone: e.target.value,
                  })
                }
                className={inputClass}
                placeholder="+44 20 1234 5678"
              />
            </div>
            <div>
              <label className={labelClass}>Website</label>
              <input
                type="url"
                value={accountFormData.accountWebsite}
                onChange={(e) =>
                  setAccountFormData({
                    ...accountFormData,
                    accountWebsite: e.target.value,
                  })
                }
                className={inputClass}
                placeholder="https://example.com"
              />
            </div>
          </div>
        </div>
      )}

      {accountOption === "existing" && selectedAccount && (
        <div className="p-4 bg-blue-600/10 border border-blue-500/20 rounded-lg">
          <h3
            className={`text-lg font-semibold mb-2 flex items-center gap-2 ${textPrimary}`}
          >
            <Building2 size={20} className="text-blue-400" />
            Selected Account
          </h3>
          <div className={`font-medium ${textPrimary}`}>
            {selectedAccount.name}
          </div>
          {selectedAccount.type && (
            <div className="text-blue-400 text-sm mt-1">
              {selectedAccount.type}
            </div>
          )}
        </div>
      )}

      {contactOption === "existing" && selectedContact && (
        <div className="p-4 bg-purple-600/10 border border-purple-500/20 rounded-lg">
          <h3
            className={`text-lg font-semibold mb-2 flex items-center gap-2 ${textPrimary}`}
          >
            <User size={20} className="text-purple-400" />
            Selected Contact
          </h3>
          <div className={`font-medium ${textPrimary}`}>
            {selectedContact.firstName} {selectedContact.lastName}
          </div>
          <div className="text-purple-400 text-sm mt-1">
            {selectedContact.email}
          </div>
        </div>
      )}
    </div>
  );

  // Step 3: Opportunity Basic Details
  const renderStep3 = () => {
    if (!createOpportunity) {
      return (
        <div className="text-center py-12">
          <Briefcase size={48} className={`mx-auto mb-4 ${textSecondary}`} />
          <p className={textSecondary}>
            Opportunity creation is disabled. Click Next to proceed with
            conversion.
          </p>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <div>
          <h3
            className={`text-lg font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}
          >
            <Briefcase size={20} className="text-green-400" />
            Opportunity Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelClass}>
                Opportunity Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="opportunityName"
                value={opportunityFormData.opportunityName}
                onChange={handleOpportunityChange}
                className={inputClass}
                placeholder="Enter opportunity name"
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Description</label>
              <textarea
                name="opportunityDescription"
                value={opportunityFormData.opportunityDescription}
                onChange={handleOpportunityChange}
                rows="3"
                className={inputClass}
                placeholder="Enter opportunity description..."
              />
            </div>
            <div>
              <label className={labelClass}>Stage</label>
              <select
                name="opportunityStage"
                value={opportunityFormData.opportunityStage}
                onChange={handleOpportunityChange}
                className={inputClass}
              >
                <option value="qualification">Qualification</option>
                <option value="needs_analysis">Needs Analysis</option>
                <option value="proposal">Proposal</option>
                <option value="negotiation">Negotiation</option>
                <option value="closed_won">Closed Won</option>
                <option value="closed_lost">Closed Lost</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Priority</label>
              <select
                name="opportunityPriority"
                value={opportunityFormData.opportunityPriority}
                onChange={handleOpportunityChange}
                className={inputClass}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Type</label>
              <input
                type="text"
                name="opportunityType"
                value={opportunityFormData.opportunityType}
                onChange={handleOpportunityChange}
                className={inputClass}
                placeholder="New Business, Renewal, Upsell"
              />
            </div>
            <div>
              <label className={labelClass}>Expected Close Date</label>
              <input
                type="date"
                name="opportunityExpectedCloseDate"
                value={opportunityFormData.opportunityExpectedCloseDate}
                onChange={handleOpportunityChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Next Step</label>
              <input
                type="text"
                name="nextStep"
                value={opportunityFormData.nextStep}
                onChange={handleOpportunityChange}
                className={inputClass}
                placeholder="Follow-up call, Send proposal"
              />
            </div>
            <div>
              <label className={labelClass}>Primary Campaign Source</label>
              <input
                type="text"
                name="primaryCampaignSource"
                value={opportunityFormData.primaryCampaignSource}
                onChange={handleOpportunityChange}
                className={inputClass}
                placeholder="Google Ads, LinkedIn Campaign"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Step 4: Financial Information
  const renderStep4 = () => {
    if (!createOpportunity) {
      return (
        <div className="text-center py-12">
          <DollarSign size={48} className={`mx-auto mb-4 ${textSecondary}`} />
          <p className={textSecondary}>
            Opportunity creation is disabled. Click Next to proceed with
            conversion.
          </p>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <div>
          <h3
            className={`text-lg font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}
          >
            <DollarSign size={20} className="text-yellow-400" />
            Financial Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Currency</label>
              <select
                name="opportunityCurrency"
                value={opportunityFormData.opportunityCurrency}
                onChange={handleOpportunityChange}
                className={inputClass}
              >
                <option value="GBP">GBP (£)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="JPY">JPY (¥)</option>
                <option value="AUD">AUD (A$)</option>
                <option value="CAD">CAD (C$)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                  {getCurrencySymbol(opportunityFormData.opportunityCurrency)}
                </span>
                <input
                  type="number"
                  name="opportunityAmount"
                  value={opportunityFormData.opportunityAmount}
                  onChange={handleOpportunityChange}
                  step="0.01"
                  className={`${inputClass} pl-10`}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Win Probability (%)</label>
              <input
                type="number"
                name="opportunityProbability"
                value={opportunityFormData.opportunityProbability}
                onChange={handleOpportunityChange}
                min="0"
                max="100"
                className={inputClass}
                placeholder="50"
              />
            </div>
            <div>
              <label className={labelClass}>Expected Revenue</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                  {getCurrencySymbol(opportunityFormData.opportunityCurrency)}
                </span>
                <input
                  type="number"
                  name="opportunityExpectedRevenue"
                  value={opportunityFormData.opportunityExpectedRevenue}
                  onChange={handleOpportunityChange}
                  step="0.01"
                  className={`${inputClass} pl-10`}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Step 5: Requirements
  const renderStep5 = () => {
    if (!createOpportunity) {
      return (
        <div className="text-center py-12">
          <Target size={48} className={`mx-auto mb-4 ${textSecondary}`} />
          <p className={textSecondary}>
            Opportunity creation is disabled. Click Next to proceed with
            conversion.
          </p>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <div>
          <h3
            className={`text-lg font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}
          >
            <Target size={20} className="text-orange-400" />
            Requirements
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                Number of Handsets / Phone Extensions
              </label>
              <input
                type="number"
                name="numberOfHandsets"
                value={opportunityFormData.numberOfHandsets}
                onChange={handleOpportunityChange}
                className={inputClass}
                placeholder="0"
              />
            </div>
            <div>
              <label className={labelClass}>
                Monthly Spend with Current Provider
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                  {getCurrencySymbol(opportunityFormData.opportunityCurrency)}
                </span>
                <input
                  type="number"
                  name="monthlySpendWithCurrentProvider"
                  value={opportunityFormData.monthlySpendWithCurrentProvider}
                  onChange={handleOpportunityChange}
                  step="0.01"
                  className={`${inputClass} pl-10`}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>
                Current Phone System Age (Years)
              </label>
              <input
                type="number"
                name="currentPhoneSystemAge"
                value={opportunityFormData.currentPhoneSystemAge}
                onChange={handleOpportunityChange}
                step="0.1"
                className={inputClass}
                placeholder="0.0"
              />
            </div>
            <div>
              <label className={labelClass}>Units Sold</label>
              <input
                type="number"
                name="unitsSold"
                value={opportunityFormData.unitsSold}
                onChange={handleOpportunityChange}
                step="0.01"
                className={inputClass}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={labelClass}>Contract Term Left (Years)</label>
              <input
                type="number"
                name="contractTermLeft"
                value={opportunityFormData.contractTermLeft}
                onChange={handleOpportunityChange}
                step="0.1"
                className={inputClass}
                placeholder="0.0"
              />
            </div>
            <div>
              <label className={labelClass}>Contract End Date</label>
              <input
                type="date"
                name="contractEndDate"
                value={opportunityFormData.contractEndDate}
                onChange={handleOpportunityChange}
                className={inputClass}
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Caller Notes</label>
              <textarea
                name="callerNotes"
                value={opportunityFormData.callerNotes}
                onChange={handleOpportunityChange}
                rows="4"
                className={inputClass}
                placeholder="Enter notes from call with prospect..."
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Step 6: Deal Score
  const renderStep6 = () => {
    if (!createOpportunity) {
      return (
        <div className="text-center py-12">
          <TrendingUp size={48} className={`mx-auto mb-4 ${textSecondary}`} />
          <p className={textSecondary}>
            Opportunity creation is disabled. Click Convert to proceed.
          </p>
        </div>
      );
    }

    const dealScoreItem = (name, label, dateName) => (
      <div className={`${panelBg} rounded-lg p-4`}>
        <label className="flex items-center gap-3 mb-2">
          <input
            type="checkbox"
            name={name}
            checked={opportunityFormData[name]}
            onChange={handleOpportunityChange}
            className="w-5 h-5 rounded"
          />
          <span className={`font-medium ${textPrimary}`}>{label}</span>
        </label>
        {opportunityFormData[name] && dateName && (
          <input
            type="date"
            name={dateName}
            value={opportunityFormData[dateName]}
            onChange={handleOpportunityChange}
            className={inputClass}
          />
        )}
      </div>
    );

    return (
      <div className="space-y-6">
        <div>
          <h3
            className={`text-lg font-semibold mb-4 flex items-center gap-2 ${textPrimary}`}
          >
            <TrendingUp size={20} className="text-pink-400" />
            Deal Score
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dealScoreItem("booked", "BOOKED", "bookedDate")}
            {dealScoreItem(
              "twoMinChallenge",
              "2 MIN CHALLENGE",
              "twoMinChallengeDate",
            )}
            {dealScoreItem("nonQualSat", "NON QUAL SAT", "satNonQualDate")}
            {dealScoreItem("qualSat", "QUAL SAT", "qualSatDate")}
            {dealScoreItem("sold", "SOLD", "soldDate")}
            {dealScoreItem(
              "installComplete",
              "Install Complete",
              "installationDate",
            )}
            {dealScoreItem("notSat", "NOT SAT", null)}
          </div>
        </div>
      </div>
    );
  };

  const totalSteps = 6;
  const canProceed = () => {
    if (step === 1) {
      if (accountOption === "existing" && !selectedAccountId) return false;
      if (contactOption === "existing" && !selectedContactId) return false;
      return true;
    }
    if (step === 2) {
      if (accountOption === "create" && !accountFormData.accountName.trim())
        return false;
      return true;
    }
    if (step === 3) {
      if (createOpportunity && !opportunityFormData.opportunityName.trim())
        return false;
      return true;
    }
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`border-b p-6 flex items-center justify-between flex-shrink-0 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div>
            <h2 className={`text-xl font-semibold ${textPrimary}`}>
              Convert Lead
            </h2>
            <p className={`text-sm mt-1 ${textSecondary}`}>
              Convert "{lead.fullName || lead.email}" to Account, Contact, and
              Opportunity
            </p>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Step Indicator */}
        <div
          className={`p-6 border-b flex-shrink-0 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: "Options" },
              { num: 2, label: "Details" },
              { num: 3, label: "Opportunity" },
              { num: 4, label: "Financial" },
              { num: 5, label: "Requirements" },
              { num: 6, label: "Deal Score" },
            ].map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
                      step === s.num
                        ? "bg-blue-600 text-white"
                        : step > s.num
                          ? "bg-green-600 text-white"
                          : isDarkMode
                            ? "bg-gray-700 text-gray-400"
                            : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {s.num}
                  </div>
                  <span
                    className={`text-xs font-medium hidden md:block ${
                      step === s.num
                        ? textPrimary
                        : step > s.num
                          ? "text-green-400"
                          : textSecondary
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {idx < 5 && (
                  <div
                    className={`flex-1 h-px mx-2 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
          {step === 6 && renderStep6()}
        </div>

        {/* Actions */}
        <div
          className={`border-t p-6 flex gap-3 flex-shrink-0 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className={`px-4 py-2.5 border rounded-lg font-medium transition-colors ${
                isDarkMode
                  ? "bg-[#13161B] border-gray-700 text-gray-300 hover:bg-gray-700"
                  : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Back
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2.5 border rounded-lg font-medium transition-colors ${
              isDarkMode
                ? "bg-[#13161B] border-gray-700 text-gray-300 hover:bg-gray-700"
                : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={() => {
              if (step < totalSteps) {
                setStep(step + 1);
              } else {
                handleConvert();
              }
            }}
            disabled={loading || !canProceed()}
            className="flex-1 px-4 py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
              border: "1px solid",
              borderImageSource:
                "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
              boxShadow: `
                0px 36px 32px -16px #427BCA36,
                0px 8px 16px 0px #2F578F1F,
                0px 2px 4px -8px #427BCA14,
                0px 8px 24px -16px #FFFFFF3D inset,
                0px -24px 32px 0px #FFFFFF38 inset,
                0px 0px 12px 0px #FFFFFF inset
              `,
            }}
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={16} />
                Converting...
              </>
            ) : step < totalSteps ? (
              "Next"
            ) : (
              "Convert Lead"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConvertLeadModal;
